import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button, Form, Input, InputNumber, Modal, Radio, Space } from 'antd';
// API
import {
  ADMIN_FUND_CAMPAIGN_FROM_BALANCE,
  ADMIN_RETURN_CAMPAIGN_BUDGET_TO_BALANCE,
} from 'api/cbBalance/mutations';
// Types
import {
  AdminFundCampaignFromBalance,
  AdminFundCampaignFromBalanceVariables,
} from 'api/cbBalance/types/AdminFundCampaignFromBalance';
import {
  AdminReturnCampaignBudgetToBalance,
  AdminReturnCampaignBudgetToBalanceVariables,
} from 'api/cbBalance/types/AdminReturnCampaignBudgetToBalance';
import {
  AdminFundCampaignFromBalanceInput,
  AdminReturnCampaignBudgetInput,
} from 'api/graphql-global-types';
import { GetCampaignsV2_getCampaigns_entities_funding } from 'api/campaignV2/types/GetCampaignsV2';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Style
import styles from './CbCampaignFundingActions.module.scss';

type Mode = 'fund' | 'return';
type AmountMode = 'standard' | 'explicit';

export type CbCampaignFundingActionsProps = {
  campaignId: string;
  funding?: GetCampaignsV2_getCampaigns_entities_funding | null;
  refetch?: () => void;
};

const formatAmount = (value: number): string => `$${value.toFixed(2)}`;

const MODE_COPY: Record<Mode, { title: string; cta: string; hint: string }> = {
  fund: {
    title: 'Fund campaign from balance',
    cta: 'Fund',
    hint: 'Moves wallet money into the campaign pot. Standard mode draws 1-1 (paid + promo); explicit mode picks exact buckets. Pot-only: a draft stays a draft.',
  },
  return: {
    title: 'Return campaign budget to balance',
    cta: 'Return',
    hint: "Moves unspent budget back to the wallet. Standard mode splits by the campaign's funding ratio; explicit mode picks exact buckets. Total is capped by budgetLeft.",
  },
};

const CbCampaignFundingActions: React.FC<CbCampaignFundingActionsProps> = ({
  campaignId,
  funding,
  refetch,
}) => {
  const [mode, setMode] = useState<Mode | null>(null);
  const [amountMode, setAmountMode] = useState<AmountMode>('standard');
  const [form] = Form.useForm();

  const [fundCampaign, { loading: fundingLoading }] = useMutation<
    AdminFundCampaignFromBalance,
    AdminFundCampaignFromBalanceVariables
  >(ADMIN_FUND_CAMPAIGN_FROM_BALANCE, {
    refetchQueries: ['GetCbAccountBalance', 'GetCbBalanceTransactions'],
  });

  const [returnBudget, { loading: returning }] = useMutation<
    AdminReturnCampaignBudgetToBalance,
    AdminReturnCampaignBudgetToBalanceVariables
  >(ADMIN_RETURN_CAMPAIGN_BUDGET_TO_BALANCE, {
    refetchQueries: ['GetCbAccountBalance', 'GetCbBalanceTransactions'],
  });

  const closeModal = () => {
    setMode(null);
    setAmountMode('standard');
    form.resetFields();
  };

  const handleSubmit = async (): Promise<void> => {
    const values = await form.validateFields();

    // amount alone XOR explicit paid/promo — enforce the exclusivity here.
    const buildInput = ():
      | AdminFundCampaignFromBalanceInput
      | AdminReturnCampaignBudgetInput => {
      if (amountMode === 'standard') {
        return {
          campaignId,
          reason: values.reason,
          amount: Number(values.amount),
        };
      }

      return {
        campaignId,
        reason: values.reason,
        paidAmount: values.paidAmount ? Number(values.paidAmount) : undefined,
        promoAmount: values.promoAmount
          ? Number(values.promoAmount)
          : undefined,
      };
    };

    if (
      amountMode === 'explicit' &&
      !values.paidAmount &&
      !values.promoAmount
    ) {
      errorNotification('Enter a paid and/or promo amount.');

      return;
    }

    try {
      if (mode === 'fund') {
        await fundCampaign({
          variables: {
            input: buildInput() as AdminFundCampaignFromBalanceInput,
          },
        });
      } else {
        await returnBudget({
          variables: {
            input: buildInput() as AdminReturnCampaignBudgetInput,
          },
        });
      }
      successNotification(
        mode === 'fund'
          ? 'Campaign funded from balance.'
          : 'Budget returned to balance.'
      );
      refetch?.();
      closeModal();
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const copy = mode ? MODE_COPY[mode] : null;

  return (
    <div className={styles.root}>
      <p className={styles.label}>Funding provenance</p>

      {funding && (
        <div className={styles.provenance}>
          <div>
            Paid: {formatAmount(funding.netPaid)} in pot (in{' '}
            {formatAmount(funding.paidIn)} / out {formatAmount(funding.paidOut)}
            )
          </div>
          <div>
            Promo: {formatAmount(funding.netPromo)} in pot (in{' '}
            {formatAmount(funding.promoIn)} / out{' '}
            {formatAmount(funding.promoOut)})
          </div>
        </div>
      )}

      <Space wrap>
        <Button onClick={() => setMode('fund')}>Fund from balance</Button>
        <Button onClick={() => setMode('return')}>Return to balance</Button>
      </Space>

      <Modal
        title={copy?.title}
        visible={mode !== null}
        okText={copy?.cta}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={fundingLoading || returning}
        destroyOnClose
      >
        <p>{copy?.hint}</p>

        <Radio.Group
          value={amountMode}
          onChange={(e) => setAmountMode(e.target.value)}
          style={{ marginBottom: 16 }}
        >
          <Radio.Button value="standard">Standard amount</Radio.Button>
          <Radio.Button value="explicit">Explicit buckets</Radio.Button>
        </Radio.Group>

        <Form form={form} layout="vertical">
          {amountMode === 'standard' ? (
            <Form.Item
              name="amount"
              label="Amount ($)"
              rules={[
                { required: true, message: 'Enter an amount' },
                {
                  type: 'number',
                  min: 0.01,
                  message: 'Must be greater than 0',
                },
              ]}
            >
              <InputNumber
                min={0.01}
                step={0.01}
                precision={2}
                style={{ width: '100%' }}
              />
            </Form.Item>
          ) : (
            <>
              <Form.Item name="paidAmount" label="Paid amount ($)">
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item name="promoAmount" label="Promo amount ($)">
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'A reason is required' }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CbCampaignFundingActions;
